import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GlobalAiIconSvgComponent } from '../svg-sprites/general/global-ai-icon-svg.component';
import { SearchComponent } from '../search/search.component';
import { RevampSvgSpriteComponent } from './revamp-svg-sprite.component';
import { OchContinueWatching, OchHeaderVm, OchNavLink, OchSearchHit, revampLuminance } from './revamp.models';

/** Reserved room for the "More" trigger when the nav overflows. */
const MORE_TRIGGER_WIDTH = 110;

/**
 * Presentational revamped header. TopMenuComponent builds the VM; this owns only
 * visual behavior (More overflow, popovers, hover underline, sticky, drawer).
 */
@Component({
  selector: 'och-header',
  templateUrl: './och-header.component.html',
  styleUrls: ['./och-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, TranslateModule, MatTooltipModule, GlobalAiIconSvgComponent, SearchComponent, RevampSvgSpriteComponent],
  host: {
    '[style.--och-primary]': 'vm().primary',
    '[style.--och-accent]': 'vm().accent',
    '[class.och-ink-dark]': 'inkDark()',
    '[class.och-acc-ink-dark]': 'accInkDark()',
    '(document:click)': 'closePops()',
    '(document:keydown.escape)': 'onEscape()',
    '(window:scroll)': 'onWindowScroll()',
    '(window:resize)': 'scheduleNavMeasure()',
  },
})
export class OchHeaderComponent implements OnDestroy {
  readonly vm = input.required<OchHeaderVm>();
  readonly navLinks = input.required<OchNavLink[]>();
  readonly continueWatching = input<OchContinueWatching | null>(null);

  readonly navAction = output<OchNavLink>();
  readonly loginClicked = output<void>();
  readonly logoutClicked = output<void>();
  readonly upgradeClicked = output<void>();
  readonly aiAssistantClicked = output<Event>();
  readonly notificationsToggled = output<void>();
  readonly popoverOpened = output<void>();
  readonly accountMenuOpened = output<void>();
  readonly searchSelected = output<OchSearchHit>();

  readonly navRef = viewChild<ElementRef<HTMLElement>>('navEl');
  readonly searchWrapRef = viewChild<ElementRef<HTMLElement>>('searchWrap');

  readonly openPop = signal<'more' | 'me' | null>(null);
  readonly drawerOpen = signal(false);
  readonly scrolled = signal(false);
  readonly hiddenIds = signal<ReadonlySet<string>>(new Set<string>());
  readonly underline = signal({ x: 0, w: 0, o: 0 });
  readonly searchOpen = signal(false);

  readonly inkDark = computed(() => revampLuminance(this.vm().primary) > 0.5);
  readonly accInkDark = computed(() => revampLuminance(this.vm().accent) > 0.55);
  readonly hiddenLinks = computed(() => {
    const hidden = this.hiddenIds();
    return this.navLinks().filter((link) => hidden.has(link.id));
  });

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  private readonly navigationEnd = toSignal(
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
  );
  private readonly langChange = toSignal(this.translate.onLangChange);

  private resizeObserver?: ResizeObserver;
  private measureTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(() => {
      this.navLinks();
      this.langChange();
      const nav = this.navRef()?.nativeElement;
      this.resizeObserver?.disconnect();
      if (nav) {
        this.resizeObserver ??= new ResizeObserver(() => this.scheduleNavMeasure());
        this.resizeObserver.observe(nav);
      }
      this.scheduleNavMeasure();
    });

    effect(() => {
      this.navigationEnd();
      // Guard redirects and browser back/forward must not leave the drawer covering the new page.
      this.openPop.set(null);
      this.drawerOpen.set(false);
      this.resetUnderline();
    });

    effect(() => {
      document.body.classList.toggle('och-drawer-open', this.drawerOpen());
    });

    // Custom fonts load after first paint, changing link widths — re-measure once ready.
    document.fonts?.ready?.then(() => this.scheduleNavMeasure());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    clearTimeout(this.measureTimeout);
    document.body.classList.remove('och-drawer-open');
  }


  scheduleNavMeasure(): void {
    clearTimeout(this.measureTimeout);
    this.measureTimeout = setTimeout(() => this.measureNav(), 50);
  }

  private measureNav(): void {
    if (!this.navRef()?.nativeElement) return;
    // Show all links, then measure natural widths after the DOM settles.
    this.hiddenIds.set(new Set<string>());
    requestAnimationFrame(() => {
      const nav = this.navRef()?.nativeElement;
      if (!nav) return;
      const items = Array.from(nav.querySelectorAll<HTMLElement>('.nl'));
      const links = this.navLinks();
      if (!items.length || items.length !== links.length) {
        this.resetUnderline();
        return;
      }
      const navStyles = getComputedStyle(nav);
      const gap = parseFloat(navStyles.columnGap) || 0;
      const available =
        nav.clientWidth - (parseFloat(navStyles.paddingLeft) || 0) - (parseFloat(navStyles.paddingRight) || 0);
      const widths = items.map((item) => item.offsetWidth);
      const totalWidth = widths.reduce((sum, w) => sum + w, 0) + gap * (widths.length - 1);
      if (totalWidth <= available) {
        this.resetUnderline();
        return;
      }
      // Keep the longest prefix that fits beside the More trigger, preserving admin order.
      const hidden = new Set<string>();
      let used = 0;
      for (let i = 0; i < links.length; i++) {
        const width = widths[i] + (i > 0 ? gap : 0);
        if (hidden.size === 0 && used + width + gap + MORE_TRIGGER_WIDTH <= available) {
          used += width;
        } else {
          hidden.add(links[i].id);
        }
      }
      this.hiddenIds.set(hidden);
      this.resetUnderline();
    });
  }


  togglePop(name: 'more' | 'me', event: Event): void {
    event.stopPropagation();
    this.openPop.update((open) => (open === name ? null : name));
    if (this.openPop() !== null) {
      this.popoverOpened.emit();
      if (name === 'me') {
        this.accountMenuOpened.emit();
      }
    }
  }

  closePops(): void {
    this.openPop.set(null);
    // Collapse on outside click only when empty, so a typed query survives a stray click.
    if (this.searchOpen() && this.searchInputEmpty()) {
      this.searchOpen.set(false);
    }
  }

  onEscape(): void {
    this.openPop.set(null);
    this.drawerOpen.set(false);
    this.searchOpen.set(false);
  }


  openSearch(event: Event): void {
    event.stopPropagation();
    this.searchOpen.set(true);
    requestAnimationFrame(() => {
      this.searchWrapRef()?.nativeElement.querySelector<HTMLInputElement>('input')?.focus();
    });
  }

  /** Keep an open search from collapsing when interacting inside it. */
  onSearchClick(event: Event): void {
    event.stopPropagation();
  }

  private searchInputEmpty(): boolean {
    const input = this.searchWrapRef()?.nativeElement.querySelector<HTMLInputElement>('input');
    return !input || !input.value.trim();
  }


  onNavHover(event: Event): void {
    const link = (event.target as HTMLElement).closest('.nl') as HTMLElement | null;
    if (!link || link.classList.contains('is-hid')) return;
    this.underline.set({ x: link.offsetLeft + 13, w: Math.max(link.offsetWidth - 26, 0), o: 1 });
  }

  resetUnderline(): void {
    requestAnimationFrame(() => {
      const nav = this.navRef()?.nativeElement;
      const active = nav?.querySelector<HTMLElement>('.nl.is-active:not(.is-hid)');
      if (active) {
        this.underline.set({ x: active.offsetLeft + 13, w: Math.max(active.offsetWidth - 26, 0), o: 1 });
      } else {
        this.underline.update((u) => ({ ...u, o: 0 }));
      }
    });
  }


  onWindowScroll(): void {
    this.scrolled.set(window.scrollY > 8);
  }


  onActionLink(link: OchNavLink, event: Event): void {
    event.preventDefault();
    this.closePops();
    this.drawerOpen.set(false);
    this.navAction.emit(link);
  }

  onLinkNavigated(): void {
    this.closePops();
    this.drawerOpen.set(false);
  }

  onNotificationsClicked(): void {
    this.closePops();
    this.notificationsToggled.emit();
  }

  trialRingStyle(): string {
    const percent = Math.max(0, Math.min(100, this.vm().trialPercent));
    return `conic-gradient(var(--acc-hi) 0 ${percent}%, color-mix(in oklab, var(--hfg) 18%, transparent) ${percent}%)`;
  }
}
